import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronRight, CornerLeftUp, ListTree, Loader2, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { tagColorClass } from "@/components/kanban/KanbanCard";
import { useTaskHierarchyCapability } from "@/hooks/use-task-hierarchy-capability";
import { useTasks } from "@/hooks/use-tasks";
import { useTenantUsers } from "@/hooks/use-tenant-users";
import type { SubtasksResponse, Task } from "@/lib/project-types";
import { TASK_STATUS_LABEL_KEYS } from "@/lib/task-status";
import { fetchWithAuth } from "@/lib/utils";
import { getTaskChange, notifyTaskChanged, TASK_CHANGED_EVENT } from "@/lib/project-events";
import { cacheScope } from "@/lib/ui-cache";
import { shouldRetainSubtaskIdempotencyKey, subtaskCreateErrorKey } from "@/lib/subtask-creation";

interface Props {
  task: Task;
  getToken: () => Promise<string>;
  archived?: boolean;
}

function hasHierarchyFields(task: Task): boolean {
  return Object.prototype.hasOwnProperty.call(task, "parent_task_id")
    && Array.isArray(task.ancestor_path)
    && Array.isArray(task.effective_tags)
    && typeof task.direct_subtask_count === "number"
    && typeof task.descendant_count === "number";
}

const ANCESTOR_TTL_MS = 60_000;
const ancestorTasks = new Map<string, { value: Task; expires: number }>();
const ancestorSessions = new Map<string, { value: boolean; expires: number }>();
const ancestorTaskRequests = new Map<string, Promise<Task>>();
const ancestorSessionRequests = new Map<string, Promise<boolean>>();
const ancestorVersions = new Map<string, number>();
const ancestorKey = (id: string) => `${cacheScope()}:${id}`;
const invalidateAncestor = (key: string) => {
  ancestorVersions.set(key, (ancestorVersions.get(key) ?? 0) + 1);
  ancestorTasks.delete(key);
  ancestorSessions.delete(key);
  ancestorTaskRequests.delete(key);
  ancestorSessionRequests.delete(key);
};

function cachedAncestorTask(id: string, getToken: () => Promise<string>): Promise<Task> {
  const key = ancestorKey(id);
  const hit = ancestorTasks.get(key);
  if (hit && hit.expires > Date.now()) return Promise.resolve(hit.value);
  const pending = ancestorTaskRequests.get(key);
  if (pending) return pending;
  const version = ancestorVersions.get(key) ?? 0;
  const request = (async () => {
    const response = await fetchWithAuth(`/api/tasks/${encodeURIComponent(id)}`, {}, getToken);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const value = await response.json() as Task;
    if ((ancestorVersions.get(key) ?? 0) === version) ancestorTasks.set(key, { value, expires: Date.now() + ANCESTOR_TTL_MS });
    return value;
  })().finally(() => { if (ancestorTaskRequests.get(key) === request) ancestorTaskRequests.delete(key); });
  ancestorTaskRequests.set(key, request);
  return request;
}

function cachedAncestorSession(id: string, getToken: () => Promise<string>): Promise<boolean> {
  const key = ancestorKey(id);
  const hit = ancestorSessions.get(key);
  if (hit && hit.expires > Date.now()) return Promise.resolve(hit.value);
  const pending = ancestorSessionRequests.get(key);
  if (pending) return pending;
  const version = ancestorVersions.get(key) ?? 0;
  const request = (async () => {
    const response = await fetchWithAuth(`/api/sessions/${encodeURIComponent(id)}`, {}, getToken);
    if (response.status === 404) return false;
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const session = await response.json() as { is_archived?: boolean };
    return session.is_archived !== true;
  })().then((value) => {
    if ((ancestorVersions.get(key) ?? 0) === version) ancestorSessions.set(key, { value, expires: Date.now() + ANCESTOR_TTL_MS });
    return value;
  }).finally(() => { if (ancestorSessionRequests.get(key) === request) ancestorSessionRequests.delete(key); });
  ancestorSessionRequests.set(key, request);
  return request;
}

if (typeof window !== "undefined") window.addEventListener(TASK_CHANGED_EVENT, (event) => {
  const task = getTaskChange(event);
  if (!task) return;
  invalidateAncestor(ancestorKey(task.id));
  if (task.session_id) invalidateAncestor(ancestorKey(task.session_id));
});

export function SubtasksSection({ task, getToken, archived = false }: Props) {
  const { t } = useTranslation("projects");
  const [, navigate] = useLocation();
  const { state: capability, retry: retryCapability } = useTaskHierarchyCapability(task.project_id, getToken);
  const { createSubtask } = useTasks(getToken);
  const { tenantUsers } = useTenantUsers(getToken);
  const [panelOpen, setPanelOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [data, setData] = useState<SubtasksResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [verifiedTask, setVerifiedTask] = useState<Task | null>(null);
  const [schemaError, setSchemaError] = useState(false);
  const [schemaRetry, setSchemaRetry] = useState(0);
  const [ancestorCheck, setAncestorCheck] = useState<"loading" | "active" | "inactive" | "error">("loading");
  const [ancestorRetry, setAncestorRetry] = useState(0);
  const requestRef = useRef(0);
  const idempotencyRef = useRef<string | null>(null);
  useEffect(() => {
    idempotencyRef.current = null;
    setCreateError(null);
  }, [task.id]);
  useEffect(() => {
    if (capability !== "supported" || hasHierarchyFields(task)) {
      setVerifiedTask(null);
      setSchemaError(false);
      return;
    }
    let active = true;
    void fetchWithAuth(`/api/tasks/${encodeURIComponent(task.id)}`, {}, getToken)
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const detail: Task = await response.json();
        if (!hasHierarchyFields(detail) || detail.id !== task.id) throw new Error("Invalid hierarchy response");
        if (active) { setVerifiedTask(detail); setSchemaError(false); }
      })
      .catch(() => { if (active) setSchemaError(true); });
    return () => { active = false; };
  }, [capability, getToken, task, schemaRetry]);

  const hierarchyTask = hasHierarchyFields(task) ? task : verifiedTask;
  useEffect(() => {
    if (capability !== "supported" || !hierarchyTask) return;
    const path = hierarchyTask.ancestor_path ?? [];
    if (path.length === 0 && !hierarchyTask.parent_task_id) { setAncestorCheck("active"); return; }
    if (path.at(-1)?.id !== hierarchyTask.parent_task_id) { setAncestorCheck("error"); return; }
    let active = true;
    setAncestorCheck("loading");
    void (async () => {
      try {
        // Bound concurrent reads for deep chains. The server still validates at create time.
        for (let offset = 0; offset < path.length; offset += 5) {
          const states = await Promise.allSettled(path.slice(offset, offset + 5).map(async (ancestor) => {
            const parent = await cachedAncestorTask(ancestor.id, getToken);
            if (parent.id !== ancestor.id || parent.project_id !== task.project_id) throw new Error("Invalid ancestor");
            if (parent.status === "cancelled") return false;
            if (!ancestor.session_id) return true;
            return cachedAncestorSession(ancestor.session_id, getToken);
          }));
          if (states.some((state) => state.status === "fulfilled" && state.value === false)) { if (active) setAncestorCheck("inactive"); return; }
          if (states.some((state) => state.status === "rejected")) throw new Error("Ancestor check failed");
        }
        if (active) setAncestorCheck("active");
      } catch { if (active) setAncestorCheck("error"); }
    })();
    return () => { active = false; };
  }, [capability, getToken, hierarchyTask, task.project_id, ancestorRetry]);

  const load = useCallback(async (before?: string | null) => {
    if (capability !== "supported") return;
    const request = ++requestRef.current;
    setLoading(true);
    setLoadError(false);
    try {
      const params = new URLSearchParams({ limit: "30" });
      if (before) params.set("before", before);
      const response = await fetchWithAuth(`/api/tasks/${encodeURIComponent(task.id)}/subtasks?${params}`, {}, getToken);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const next: SubtasksResponse = await response.json();
      if (!Array.isArray(next.tasks)
        || [next.direct_subtask_count, next.direct_subtask_done_count, next.direct_subtask_open_count,
          next.descendant_count, next.descendant_done_count, next.descendant_open_count].some((count) => typeof count !== "number")
        || (next.has_more && !next.next_cursor)) throw new Error("Invalid subtask response");
      if (request !== requestRef.current) return;
      setData((previous) => before && previous ? {
        ...next,
        tasks: [...previous.tasks, ...next.tasks.filter((child) => !previous.tasks.some((row) => row.id === child.id))],
      } : next);
    } catch {
      if (request === requestRef.current) setLoadError(true);
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [capability, getToken, task.id]);

  useEffect(() => {
    if (panelOpen && capability === "supported") void load();
    // Ignore in-flight reads after closing, changing tasks, or unmounting.
    return () => { requestRef.current += 1; };
  }, [capability, load, panelOpen]);
  const disabled = task.status === "cancelled" || archived || ancestorCheck === "inactive";
  const visibleTags = hierarchyTask?.effective_tags
    ? Array.from(hierarchyTask.effective_tags.reduce((map, entry) => {
      if (!map.has(entry.tag.id) || !entry.inherited) map.set(entry.tag.id, entry);
      return map;
    }, new Map<string, NonNullable<Task["effective_tags"]>[number]>()).values())
    : task.tags.map((tag) => ({ tag, inherited: false, source_task: null }));
  const create = async () => {
    if (pending || disabled || !hierarchyTask || capability !== "supported" || ancestorCheck !== "active") return;
    setPending(true);
    setCreateError(null);
    // Retrying an ambiguous result must replay the same request, not create a second child.
    const key = idempotencyRef.current ?? crypto.randomUUID();
    idempotencyRef.current = key;
    try {
      const result = await createSubtask(task.project_id, task.id, t("subtasks.new"), key, "default");
      if (!result.task?.session_id) {
        if (!shouldRetainSubtaskIdempotencyKey(result.status)) idempotencyRef.current = null;
        setCreateError(t(subtaskCreateErrorKey(result.status)));
        return;
      }
      idempotencyRef.current = null;
      notifyTaskChanged(result.task);
      navigate(`/project/${encodeURIComponent(task.project_id)}?task=${encodeURIComponent(result.task.id)}`);
    } finally {
      setPending(false);
    }
  };

  const canCreate = capability === "supported" && !disabled && Boolean(hierarchyTask) && ancestorCheck === "active";
  const count = data?.direct_subtask_count ?? hierarchyTask?.direct_subtask_count;

  return <Popover open={panelOpen} onOpenChange={setPanelOpen}>
    <PopoverTrigger asChild>
      <Button type="button" variant="ghost" size="sm" className="h-8 shrink-0 gap-1 px-2 text-muted-foreground"
        aria-label={t("subtasks.sectionTitle")} title={t("subtasks.sectionTitle")} aria-expanded={panelOpen}>
        <ListTree className="size-4" aria-hidden="true" />
        <span aria-live="polite">
          {count !== undefined && <>
            <span aria-hidden="true" className="text-xs tabular-nums">{count}</span>
            <span className="sr-only">{t("subtasks.cardDirectCount", { count })}</span>
          </>}
        </span>
      </Button>
    </PopoverTrigger>
    <PopoverContent align="end" className="w-[26rem] max-w-[calc(100vw-2rem)] max-h-[min(70dvh,var(--radix-popover-content-available-height))] space-y-3 overflow-y-auto p-3 text-sm"
      aria-label={t("subtasks.sectionTitle")}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-medium">{t("subtasks.sectionTitle")}</h3>
        {data && <span className="text-xs text-muted-foreground" aria-live="polite">{t("subtasks.directProgress", { done: data.direct_subtask_done_count, total: data.direct_subtask_count })}</span>}
      </div>
      {hierarchyTask?.parent_task_id && (hierarchyTask.ancestor_path?.at(-1)?.id === hierarchyTask.parent_task_id
        ? <Button type="button" size="sm" variant="ghost" className="h-auto max-w-full justify-start whitespace-normal px-1 text-left text-xs text-muted-foreground" onClick={() => navigate(`/project/${encodeURIComponent(task.project_id)}?task=${encodeURIComponent(hierarchyTask.parent_task_id!)}`)}><CornerLeftUp className="size-3.5" aria-hidden="true" />{t("subtasks.parentChip", { title: hierarchyTask.ancestor_path?.at(-1)?.title })}</Button>
        : <p className="text-xs text-muted-foreground">{t("subtasks.parentUnavailable")}</p>)}
      {visibleTags.length > 0 && <div className="flex flex-wrap gap-1">{visibleTags.map(({ tag, inherited, source_task }) => <Badge key={tag.id} variant="outline" className={inherited ? "border-border bg-transparent text-muted-foreground" : tagColorClass(tag.color)} title={inherited ? t("subtasks.inheritedTagSource", { title: source_task?.title ?? t("subtasks.parentUnavailable") }) : tag.name}>{tag.name}</Badge>)}</div>}
      {capability === "loading" && <p role="status" className="text-muted-foreground">{t("loadingTasks")}</p>}
      {capability === "unsupported" && <p className="text-muted-foreground">{t("subtasks.serverUnsupported")}</p>}
      {capability === "error" && <p role="alert" className="text-destructive">{t("subtasks.capabilityFailed")} <Button type="button" variant="link" size="sm" onClick={() => void retryCapability()}>{t("common:actions.retry")}</Button></p>}
      {capability === "supported" && <>
        {schemaError && <p role="alert" className="text-destructive">{t("subtasks.schemaFailed")} <Button type="button" variant="link" size="sm" onClick={() => setSchemaRetry((value) => value + 1)}>{t("common:actions.retry")}</Button></p>}
        {ancestorCheck === "error" && <p role="alert" className="text-destructive">{t("subtasks.ancestorCheckFailed")} <Button type="button" variant="link" size="sm" onClick={() => setAncestorRetry((value) => value + 1)}>{t("common:actions.retry")}</Button></p>}
        {((loading && !data) || (!hierarchyTask && !schemaError)) && <p role="status" className="text-muted-foreground">{t("loadingTasks")}</p>}
        {data && <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" aria-live="polite">
          <span>{t("subtasks.descendantProgress", { done: data.descendant_done_count, total: data.descendant_count })}</span>
          <span>{t("subtasks.openDescendants", { count: data.descendant_open_count })}</span>
        </div>}
        {data?.tasks.length === 0 && <p className="text-muted-foreground">{t("subtasks.empty")}</p>}
        {Boolean(data?.tasks.length) && <ul className="divide-y">
          {data?.tasks.map((child) => <li key={child.id}>
            <button type="button" className="flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => navigate(`/project/${encodeURIComponent(child.project_id)}?task=${encodeURIComponent(child.id)}`)}
              aria-label={t("subtasks.openChild", { title: child.title })}>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium" title={child.title}>{child.title}</span>
                <span className="flex min-w-0 gap-2 text-xs text-muted-foreground">
                  <span className="shrink-0">{t(TASK_STATUS_LABEL_KEYS[child.status])}</span>
                  <span className="truncate">{tenantUsers.find((user) => user.id === child.assigned_to)?.display_name ?? tenantUsers.find((user) => user.id === child.assigned_to)?.email ?? child.assigned_to ?? t("unassigned")}</span>
                </span>
              </span>
              <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
            </button>
          </li>)}
        </ul>}
        {data?.has_more && <Button type="button" size="sm" variant="ghost" className="w-full" disabled={loading} onClick={() => void load(data.next_cursor)}>
          {loading && <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />}{t("subtasks.loadMore")}
        </Button>}
        {loadError && <p role="alert" className="text-destructive">{t("subtasks.loadFailed")} <Button type="button" variant="link" size="sm" onClick={() => void load()}>{t("common:actions.retry")}</Button></p>}
      </>}
      {capability === "supported" && <div className="border-t pt-3">
        <Button type="button" size="sm" variant="outline" className="w-full" disabled={pending || !canCreate} title={disabled ? t("subtasks.ancestorInactive") : undefined} onClick={() => { void create(); }}>
          {pending ? <Loader2 className="size-3.5 animate-spin" aria-hidden="true" /> : <Plus className="size-3.5" aria-hidden="true" />}
          {t(pending ? "subtasks.creating" : "subtasks.new")}
        </Button>
        {createError && <p role="alert" className="mt-2 text-xs text-destructive">{createError}</p>}
        {disabled && <p className="text-xs text-muted-foreground">{t("subtasks.ancestorInactive")}</p>}
      </div>}
    </PopoverContent>
  </Popover>;
}
